import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { EffectTab } from "@/data/convert-effects.data";
import useSelection from "@/hooks/use-selection";
import { ConvertBaseSettings } from "@/hooks/ai-tool-process/use-convert-base-context-provider";

type ConversionEffectProps<TSettings extends ConvertBaseSettings> = {
  settings: TSettings;
  setSettings: Dispatch<SetStateAction<TSettings>>;
};

export const useConversionEffects = <TSettings extends ConvertBaseSettings>({
  settings,
  setSettings,
}: ConversionEffectProps<TSettings>) => {
  const [selectedEffectTabId, setSelectedEffectTabId] = useState<string>();

  const selectedEffectTab = useMemo(() => {
    return selectedEffectTabId
      ? settings.conversionEffects[selectedEffectTabId]
      : undefined;
  }, [settings, selectedEffectTabId]);

  const effectTabs = useMemo(() => {
    const conversionEffects = settings.conversionEffects;

    return [conversionEffects.reverbSettings, conversionEffects.pitchSettings];
  }, [settings]);

  const isTabSelected = (tab: EffectTab) => {
    return tab.id === selectedEffectTabId;
  };

  const tabsEnabledSelection = useSelection(effectTabs);

  const toggleEnabled = (tab: EffectTab) =>
    tabsEnabledSelection.toggleSelection(tab.id);
  const isTabEnabled = (tab: EffectTab) =>
    tabsEnabledSelection.isSelected(tab.id);
  const enabledTabs = tabsEnabledSelection.selectedItems;

  const handleResetAllEffectTabsClick = () => {
    const resetSettings = { ...settings };

    effectTabs.forEach((tab) => {
      if (isTabEnabled(tab) !== tab.isEnabledByDefault) {
        toggleEffectTabEnabledState(tab);
      }

      resetEffectTabParameters(tab, resetSettings);
    });

    setSettings(resetSettings);
  };

  const handleResetEffectTabParametersClick = (tab: EffectTab) => {
    const resetSettings = { ...settings };
    resetEffectTabParameters(tab, resetSettings);
    setSettings(resetSettings);
  };

  const resetEffectTabParameters = (
    tab: EffectTab,
    resetSettings: TSettings,
  ) => {
    const tabId = tab.id;

    const parameterIds = tab.getParameters().map((parameter) => parameter.id);

    parameterIds.forEach((parameterId) => {
      resetSettings.conversionEffects = {
        ...resetSettings.conversionEffects,
        [tabId]: {
          ...resetSettings.conversionEffects[tabId],
          [parameterId]: {
            ...resetSettings.conversionEffects[tabId][parameterId],
            value:
              resetSettings.conversionEffects[tabId][parameterId].defaultValue,
          },
        },
      };
    });
  };

  const handleTabSelection = (tab: EffectTab) => {
    setSelectedEffectTabId(tab.id);

    if (!isTabEnabled(tab)) {
      toggleEnabled(tab);
    }
  };

  const handleEffectTabSwitcherClick = (tab: EffectTab) => {
    toggleEffectTabEnabledState(tab);
  };

  const toggleEffectTabEnabledState = (tab: EffectTab) => {
    if (selectedEffectTabId === tab.id) {
      setSelectedEffectTabId(undefined);
    }

    toggleEnabled(tab);
  };

  const handleEffectParameterValueChanged = (
    tabId: string,
    parameterId: string,
    value: number,
  ) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      conversionEffects: {
        ...prevSettings.conversionEffects,
        [tabId]: {
          ...prevSettings.conversionEffects[tabId],
          [parameterId]: {
            ...prevSettings.conversionEffects[tabId][parameterId],
            value: value,
          },
        },
      },
    }));
  };

  return {
    effectTabs,
    enabledTabs,
    selectedEffectTab,
    isTabSelected,
    isTabEnabled,
    handleTabSelection,
    handleEffectTabSwitcherClick,
    handleEffectParameterValueChanged,
    handleResetAllEffectTabsClick,
    handleResetEffectTabParametersClick,
    setSelectedEffectTabId,
  };
};
